import React, { useEffect, useMemo, useState } from 'react';
import { Layout, Menu, Avatar, Dropdown, Button, theme } from 'antd';
import type { MenuProps } from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { routeConfig, RouteConfigItem } from '../../config/routes';
import './MainLayout.css';

const { Header, Sider, Content } = Layout;
const MENU_OPEN_KEYS_STORAGE_KEY = 'main-layout-open-keys';

const buildRouteParentMap = (items: RouteConfigItem[], parentKeys: string[] = [], map = new Map<string, string[]>()) => {
  items.forEach((item) => {
    map.set(item.key, parentKeys);
    if (item.children?.length) {
      buildRouteParentMap(item.children, [...parentKeys, item.key], map);
    }
  });
  return map;
};

const MainLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { staff, can, logout } = useAuth();
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const filterMenu = (items: RouteConfigItem[]): RouteConfigItem[] => {
    return items.reduce<RouteConfigItem[]>((acc, item) => {
      if (item.hideInMenu) {
        return acc;
      }
      const children = item.children ? filterMenu(item.children) : undefined;
      const hasPermission = !item.permission || can(item.permission);
      
      // Show if user has permission, AND (it's a leaf node OR it has visible children)
      // Note: If a parent has permission but no children (and originally had children), maybe hide it?
      // But here we check if children exists after filtering.
      if (hasPermission) {
          if (item.children && (!children || children.length === 0)) {
              // If it had children but all were filtered out, don't show parent unless parent itself is clickable (usually not for submenus)
              // But if it is a category like "System Management", we probably don't want to show it empty.
              // For now, let's assume if children is empty and it was a group, hide it.
              // However, routeConfig structure: groups have no component.
              if (!item.component) {
                  return acc;
              }
          }
          acc.push({ ...item, children });
      }
      return acc;
    }, []);
  };

  const toAntdMenuItems = (items: RouteConfigItem[]): MenuProps['items'] => {
    return items.map((item) => ({
      key: item.key,
      icon: item.icon,
      label: item.label,
      children: item.children ? toAntdMenuItems(item.children) : undefined,
    }));
  };

  const visibleMenu = useMemo(() => filterMenu(routeConfig), [can]);
  const menuItems = useMemo(() => toAntdMenuItems(visibleMenu), [visibleMenu]);
  const routeParentMap = useMemo(() => buildRouteParentMap(visibleMenu), [visibleMenu]);
  const [openKeys, setOpenKeys] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(MENU_OPEN_KEYS_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    const currentParents = routeParentMap.get(location.pathname) || [];
    if (!currentParents.length) {
      return;
    }
    setOpenKeys((prev) => Array.from(new Set([...prev, ...currentParents])));
  }, [location.pathname, routeParentMap]);

  useEffect(() => {
    try {
      localStorage.setItem(MENU_OPEN_KEYS_STORAGE_KEY, JSON.stringify(openKeys));
    } catch {
      // ignore localStorage write errors
    }
  }, [openKeys]);

  const username = staff?.name || staff?.username || '管理员';

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: '个人资料',
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: '设置',
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: () => {
        logout();
        navigate('/login');
      },
    },
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
  };

  return (
    <Layout className="main-layout">
      <Sider 
        trigger={null} 
        collapsible 
        collapsed={collapsed}
        className="sidebar"
        style={{ background: '#fff' }}
      >
        <div className="logo" />
        <Menu
          theme="light"
          mode="inline"
          selectedKeys={[location.pathname]}
          openKeys={collapsed ? [] : openKeys}
          items={menuItems}
          onClick={handleMenuClick}
          onOpenChange={(keys) => setOpenKeys(keys as string[])}
        />
      </Sider>
      
      <Layout style={{ marginLeft: collapsed ? 80 : 200, transition: 'margin-left 0.2s' }}>
        <Header 
          className="header"
          style={{ 
            padding: 0, 
            background: '#1677ff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingRight: '24px',
            position: 'fixed',
            top: 0,
            right: 0,
            left: 0,
            width: '100vw',
            zIndex: 999,
            transition: 'width 0.2s'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{
                fontSize: '16px',
                width: 64,
                height: 64,
                color: '#fff'
              }}
            />
            <div style={{ color: '#fff', fontSize: 18, fontWeight: 600 }}>
              租赁业务风控系统
            </div>
          </div>
          
          <div className="header-right">
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <div className="user-info">
                <Avatar size="small" icon={<UserOutlined />} />
                <span className="username" style={{ color: '#fff' }}>{username}</span>
              </div>
            </Dropdown>
          </div>
        </Header>
        
        <Content
          style={{
            margin: '70px 16px 24px 16px',
            padding: 0,
            minHeight: 'calc(100vh - 94px)',
            background: 'transparent',
            overflow: 'auto'
          }}
        >
          <div className="content-inner">
            <Outlet />
          </div>
        </Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout;
